import { Radio, Tooltip } from "antd"
import { formatDimensionEntries } from "helpers/modelNumberMatch.helper"
import {
	defaultTooltipStyles,
	getShadowTooltipContainer,
} from "components/ui/customWebviewer/shared/shadowTooltip"
import type { TModelNumber } from "types/modelNumbers.type"
import type { TMatchResult } from "helpers/modelNumberMatch.helper"
import style from "./ModelNumberRow.module.scss"

const {
	row,
	rowSelected,
	radio,
	thumbnail,
	body,
	modelNumber,
	dims,
	dimCell,
	dimLabel,
	dimValue,
	dimValueOut,
	tags,
	tag,
	tagMl,
	tagBest,
	tagPartial,
	tagNone,
	tagNoPrice,
} = style

type Props = {
	model: TModelNumber
	isSelected: boolean
	isMlChoice: boolean
	match: TMatchResult
	hasPrice?: boolean
	thumbnailUrl?: string | null
	onSelect: (id: number) => void
}

const TAG_TOOLTIPS = {
	ml: "Recommended by the machine learning model",
	best: "All dimensions (W, H, D) fall within the entered range",
	partial: "One or more dimensions are outside the target range",
	none: "This model does not meet the entered cabinet dimensions",
	noPrice: "No pricing data. Contact admin before adding to quotation",
}

export const ModelNumberRow = ({
	model,
	isSelected,
	isMlChoice,
	match,
	hasPrice = true,
	thumbnailUrl = null,
	onSelect,
}: Props) => {
	const dimsCells = [
		{ label: "W:", value: formatDimensionEntries(model.dimensions.width), inRange: match.inRange.width },
		{ label: "H:", value: formatDimensionEntries(model.dimensions.height), inRange: match.inRange.height },
		{ label: "D:", value: formatDimensionEntries(model.dimensions.depth), inRange: match.inRange.depth },
	]

	return (
		<div className={`${row} ${isSelected ? rowSelected : ""}`} onClick={() => onSelect(model.id)}>
			<Radio className={radio} checked={isSelected} value={model.id} />
			{thumbnailUrl ? <img className={thumbnail} src={thumbnailUrl} alt="" /> : null}
			<div className={body}>
				<span className={modelNumber}>{model.model_number}</span>
				<div className={dims}>
					{dimsCells.map((cell) => (
						<span key={cell.label} className={dimCell}>
							<span className={dimLabel}>{cell.label}</span>
							<span className={cell.inRange ? dimValue : dimValueOut}>{cell.value}</span>
						</span>
					))}
				</div>
			</div>
			<div className={tags}>
				{isMlChoice ? (
					<Tooltip title={TAG_TOOLTIPS.ml} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
						<span className={`${tag} ${tagMl}`}>ML choice</span>
					</Tooltip>
				) : null}
				{match.tag === "best" ? (
					<Tooltip title={TAG_TOOLTIPS.best} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
						<span className={`${tag} ${tagBest}`}>Best match</span>
					</Tooltip>
				) : null}
				{match.tag === "partial" ? (
					<Tooltip title={TAG_TOOLTIPS.partial} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
						<span className={`${tag} ${tagPartial}`}>Partial match</span>
					</Tooltip>
				) : null}
				{!hasPrice ? (
					<Tooltip title={TAG_TOOLTIPS.noPrice} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
						<span className={`${tag} ${tagNoPrice}`}>No price</span>
					</Tooltip>
				) : null}
				{match.tag === "none" ? (
					<Tooltip title={TAG_TOOLTIPS.none} placement="top" getPopupContainer={getShadowTooltipContainer} styles={defaultTooltipStyles}>
						<span className={`${tag} ${tagNone}`}>No match</span>
					</Tooltip>
				) : null}
			</div>
		</div>
	)
}
